import { useCallback, useEffect, useRef, useState } from "react";
import {
  InvestigationAttachment,
  Stage,
} from "../lusca-search-api/generated-src";

/** Drafts older than this are dropped on read so localStorage doesn't collect stale notes. */
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  text: string;
  attachments: InvestigationAttachment[];
  savedAt: number;
}

export const commentDraftKey = (stage: Stage, investigationId: string) =>
  `lusca-comment-draft-${stage}-${investigationId}`;

const readDraft = (key: string): StoredDraft | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return {
      text: typeof parsed.text === "string" ? parsed.text : "",
      attachments: Array.isArray(parsed.attachments)
        ? parsed.attachments.filter((a) => a && typeof a.key === "string")
        : [],
      savedAt: parsed.savedAt,
    };
  } catch {
    return null;
  }
};

/**
 * An unposted comment for one investigation, kept in localStorage so it survives switching tabs
 * (which unmounts the Comments tab), a refresh, or closing the browser tab. Attachments are kept
 * as the S3 keys already uploaded, so they don't need re-uploading.
 */
export function useCommentDraft(stage: Stage, investigationId: string) {
  const key = commentDraftKey(stage, investigationId);
  const [initial] = useState(() => readDraft(key));
  const [text, setText] = useState(initial?.text ?? "");
  const attachmentsRef = useRef<InvestigationAttachment[]>(
    initial?.attachments ?? []
  );

  const persist = useCallback(
    (nextText: string, attachments: InvestigationAttachment[]) => {
      try {
        if (!nextText.trim() && attachments.length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(
            key,
            JSON.stringify({ text: nextText, attachments, savedAt: Date.now() })
          );
        }
      } catch {
        // Storage full/disabled: the draft still lives in memory for this page.
      }
    },
    [key]
  );

  const updateText = useCallback(
    (next: string) => {
      setText(next);
      persist(next, attachmentsRef.current);
    },
    [persist]
  );

  const textRef = useRef(text);
  textRef.current = text;

  const setAttachments = useCallback(
    (attachments: InvestigationAttachment[]) => {
      attachmentsRef.current = attachments;
      persist(textRef.current, attachments);
    },
    [persist]
  );

  const clear = useCallback(() => {
    setText("");
    attachmentsRef.current = [];
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  // Another browser tab editing the same draft: pick up its text so the two don't fight.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      setText(readDraft(key)?.text ?? "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key]);

  return {
    text,
    setText: updateText,
    initialAttachments: initial?.attachments ?? [],
    setAttachments,
    clear,
    /** True when this mount picked up a draft saved earlier (tab switch, refresh, reopen). */
    restored:
      !!initial && (!!initial.text.trim() || initial.attachments.length > 0),
  };
}
